import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { format, isAfter } from "date-fns";
import { useFragment } from "react-relay";

import {
  CalendarFilledIcon,
  ClockIcon,
  CoinsIcon,
  DollarBillIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import {
  useGetMinimumCommitment,
  useGetPackagePriceMap,
} from "@whoosh/features/src/Packages";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";
import { convertDateStringToObj } from "@whoosh/utilities/src/dates";

import { StorePackageSummary_package$key } from "./__generated__/StorePackageSummary_package.graphql";

type Props = {
  packageRef?: StorePackageSummary_package$key | null;
  hideLabel?: boolean;
  hidePrices?: boolean;
};
export const StorePackageSummary = ({
  packageRef,
  hideLabel,
  hidePrices,
}: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const pkg = useFragment(StorePackageSummaryFragmentString, packageRef);

  const isSubscription = pkg?.billingType === "SUBSCRIPTION";

  const priceMap = useGetPackagePriceMap(pkg);
  const monthlyPrice = priceMap?.["MONTHLY"];
  const oneTimePrice = priceMap?.["ONE_TIME"];

  const minimumCommitment = useGetMinimumCommitment(pkg);
  const hasMinimumCommitment = minimumCommitment > 0;

  const today = new Date();
  const startDate = convertDateStringToObj(pkg?.startOn);
  const startDateStr = isAfter(today, startDate)
    ? t`Today`
    : format(startDate, "MMM do, yyyy");

  const endDate = pkg?.endOn
    ? format(convertDateStringToObj(pkg.endOn), "MMM do, yyyy")
    : undefined;
  const isPeriodAfterActivation =
    pkg?.activeUntilPeriodType === "PERIOD_AFTER_ACTIVATION";
  const activeUntilPeriod = pkg?.activeUntilPeriod;
  const activeUntilPeriodUnit = pkg?.activeUntilPeriodUnit;

  const subscriptionLength =
    isPeriodAfterActivation && activeUntilPeriod && activeUntilPeriodUnit
      ? `${activeUntilPeriod} ${getDurationLabel(
          activeUntilPeriodUnit,
          activeUntilPeriod
        )}`
      : undefined;

  return (
    <VStack gap={3}>
      {hideLabel ? null : (
        <Text
          color="text-secondary"
          variant="mainTiny"
          textTransform="uppercase"
        >{t`Membership Details`}</Text>
      )}
      <VStack gap={3}>
        {oneTimePrice !== undefined && !hidePrices ? (
          <Row
            Icon={isSubscription ? <CoinsIcon /> : <DollarBillIcon />}
            Label={<Text>{isSubscription ? t`One-time Fee` : t`Price`}</Text>}
            Value={
              <Text>
                {currencySymbol}
                {ConvertCentsToDollars(oneTimePrice)}
              </Text>
            }
          />
        ) : null}
        {monthlyPrice !== undefined && !hidePrices ? (
          <Row
            Icon={<DollarBillIcon />}
            Label={<Text>{t`Subscription Fee`}</Text>}
            Value={
              <Text>
                {currencySymbol}
                {ConvertCentsToDollars(monthlyPrice)}{" "}
                <Text color="text-secondary">{t`/ month`}</Text>
              </Text>
            }
          />
        ) : null}
        {hasMinimumCommitment ? (
          <Row
            Icon={<ClockIcon />}
            Label={<Text>{t`Minimum Commitment`}</Text>}
            Value={<Text>{`${minimumCommitment} ${t`Months`}`}</Text>}
          />
        ) : null}
        <Row
          Icon={<CalendarFilledIcon />}
          Label={<Text>{t`Start Date`}</Text>}
          Value={<Text>{startDateStr}</Text>}
        />
        {subscriptionLength ? (
          <Row
            Icon={<CalendarFilledIcon />}
            Label={<Text>{t`Subscription Length`}</Text>}
            Value={<Text>{subscriptionLength}</Text>}
          />
        ) : null}
        {endDate && !isPeriodAfterActivation ? (
          <Row
            Icon={<CalendarFilledIcon />}
            Label={<Text>{t`End Date`}</Text>}
            Value={<Text>{endDate}</Text>}
          />
        ) : null}
      </VStack>
    </VStack>
  );
};

const StorePackageSummaryFragmentString = graphql`
  fragment StorePackageSummary_package on Package {
    id
    __typename
    billingType
    startOn
    endOn
    activeUntilPeriod
    activeUntilPeriodUnit
    activeUntilPeriodType
    ...useGetPackagePriceMap_package
    ...useGetMinimumCommitment_package
  }
`;

const getDurationLabel = (
  duration: "DAY" | "WEEK" | "MONTH" | "YEAR",
  count: number
) => {
  if (duration === "DAY") {
    return count === 1 ? t`day` : t`days`;
  }
  if (duration === "WEEK") {
    return count === 1 ? t`week` : t`weeks`;
  }
  if (duration === "MONTH") {
    return count === 1 ? t`month` : t`months`;
  }
  return count === 1 ? t`year` : t`years`;
};

type RowProps = {
  Icon: React.ReactNode;
  Label: React.ReactNode;
  Value: React.ReactNode;
};
const Row = ({ Icon, Label, Value }: RowProps) => {
  return (
    <HStack gap={2} alignItems="center">
      <HStack gap={2} flex={1}>
        {Icon}
        {Label}
      </HStack>
      {Value}
    </HStack>
  );
};
